import { useState } from "react";

import { GlobeIcon, NewspaperIcon } from "@lucide/icons";

import { formatDate } from "simplydo/core/dates";
import { H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";

import type {
  CompanyArticle,
  CompanyNewsItem,
} from "components/innointel/types";
import { FakeLink } from "components/lib/UI";

import {
  InfoBoxWidget,
  InfoBoxWidgetTitleArea,
  InfoContainer,
  InfoItem,
  KeyFactLink,
} from "../Styles";

type CompanyArticlesProps = {
  numArticles?: number;
  articles: CompanyArticle[];
  news: CompanyNewsItem[];
};

const CompanyArticles = ({
  numArticles,
  articles,
  news,
}: CompanyArticlesProps) => {
  const [viewingMore, setViewingMore] = useState(false);
  const [viewingMoreNews, setViewingMoreNews] = useState(false);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {articles.length ? (
        <InfoBoxWidget $sectioned>
          <InfoBoxWidgetTitleArea>
            <Icon icon={NewspaperIcon} />
            <div>
              <H3>Articles</H3>
              <span>{numArticles ?? articles.length} found</span>
            </div>
          </InfoBoxWidgetTitleArea>
          <InfoContainer $wrapped>
            {articles.slice(0, viewingMore ? 30 : 10).map((article, index) => (
              <InfoItem
                key={article.url ?? `${article.title}-${index}`}
                href={article.url}
              >
                <KeyFactLink
                  title={article.title ?? "Untitled"}
                  fact={
                    article.postedAt
                      ? `Published: ${formatDate(article.postedAt, "yyyy-MM-dd HH:mm")}`
                      : "Published date unknown"
                  }
                  image={article.imageUrl}
                  imageAltContent={<Icon icon={NewspaperIcon} size="large" />}
                />
              </InfoItem>
            ))}
          </InfoContainer>
          {articles.length > 9 && (
            <div style={{ textAlign: "center" }}>
              <FakeLink onClick={() => setViewingMore(!viewingMore)} withBorder>
                View {viewingMore ? "fewer" : "more"} articles
              </FakeLink>
            </div>
          )}
        </InfoBoxWidget>
      ) : null}
      {news.length ? (
        <InfoBoxWidget $sectioned style={{ marginLeft: 0 }}>
          <InfoBoxWidgetTitleArea>
            <Icon icon={GlobeIcon} />
            <H3>News</H3>
            <span>{news.length} found</span>
          </InfoBoxWidgetTitleArea>
          <InfoContainer $wrapped>
            {news.slice(0, viewingMoreNews ? 30 : 10).map((article, index) => (
              <InfoItem
                key={article._id ?? article.url ?? `${article.title}-${index}`}
                href={article.url}
              >
                <KeyFactLink
                  title={article.title ?? "Untitled"}
                  fact={
                    article.postedAt
                      ? `Published: ${formatDate(article.postedAt, "yyyy-MM-dd HH:mm")}`
                      : "Published date unknown"
                  }
                  image={article.imageUrl}
                  imageAltContent={<Icon icon={NewspaperIcon} size="large" />}
                />
              </InfoItem>
            ))}
          </InfoContainer>
          {news.length > 9 && (
            <div style={{ textAlign: "center" }}>
              <FakeLink
                onClick={() => setViewingMoreNews(!viewingMoreNews)}
                withBorder
              >
                View {viewingMoreNews ? "fewer" : "more"} news
              </FakeLink>
            </div>
          )}
        </InfoBoxWidget>
      ) : null}
    </div>
  );
};

export default CompanyArticles;
